import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { BookOpen, ShieldCheck, User, ChevronRight, ImageIcon, Users } from "lucide-react";
import { PageHero, PageBody, Lightbox, slugify, FONT } from "./_shared";
import { useBul } from "./BulLayout";
import PhotoCarousel from "@/components/PhotoCarousel";

// Ink-heavy palette — every department card gets the same ink background with
// gold accent to keep the Southwestern-inspired identity consistent.
const GRADIENTS = [
  "from-[#0F0F10] to-[#1F1F20]",
  "from-[#141312] to-[#0F0F10]",
  "from-[#1F1F20] to-[#0F0F10]",
  "from-[#0F0F10] to-[#141312]",
  "from-[#0F0F10] to-[#1F1F20]",
  "from-[#141312] to-[#0F0F10]",
  "from-[#1F1F20] to-[#0F0F10]",
  "from-[#0F0F10] to-[#141312]",
];

export default function Departments() {
  const data = useBul();
  // Already sorted by `order` asc on the backend, but defend just in case.
  // useMemo keeps the reference stable across renders — otherwise the sync-effect
  // below would fire on every render and override clicks on the sidebar tabs.
  const list = useMemo(
    () =>
      (data.departments || [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [data.departments]
  );
  const allStaff = data.staff || [];
  const [searchParams] = useSearchParams();
  const { slug } = useParams();
  const queryDeptId = searchParams.get("d");
  const findBySlug = (s) =>
    s ? list.find((d) => slugify(d.name) === s.toLowerCase()) : null;
  const initialId =
    findBySlug(slug)?.id ||
    (queryDeptId && list.some((d) => d.id === queryDeptId)
      ? queryDeptId
      : list[0]?.id) ||
    null;
  const [activeId, setActiveId] = useState(initialId);
  const [lbIdx, setLbIdx] = useState(-1);

  // Sync selection when route slug / query param changes
  useEffect(() => {
    const bySlug = findBySlug(slug);
    if (bySlug) {
      setActiveId(bySlug.id);
      setLbIdx(-1);
      return;
    }
    if (queryDeptId && list.some((d) => d.id === queryDeptId)) {
      setActiveId(queryDeptId);
      setLbIdx(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, queryDeptId, list]);

  const active = list.find((d) => d.id === activeId) || null;
  const activeIdx = list.findIndex((d) => d.id === activeId);
  // Reserved for future accent styling; the ink header is now uniform.
  // eslint-disable-next-line no-unused-vars
  const _grad = GRADIENTS[Math.max(0, activeIdx) % GRADIENTS.length];
  const photos = active?.photos || [];
  const isHodByDesignation = (s) => {
    const d = (s?.designation || "").toLowerCase();
    return /\b(hod|head\s+of\s+department)\b/i.test(d) || d.includes("head of department");
  };
  const deptStaff = allStaff
    .filter((s) => active && s.department_id === active.id)
    .sort((a, b) => {
      // 1. HOD by explicit link
      const hodId = active?.head_of_department_id;
      if (hodId) {
        if (a.id === hodId) return -1;
        if (b.id === hodId) return 1;
      }
      // 2. HOD by designation (fallback when explicit link is missing)
      const aH = isHodByDesignation(a);
      const bH = isHodByDesignation(b);
      if (aH && !bH) return -1;
      if (!aH && bH) return 1;
      // 3. Alphabetical by name
      return (a.name || "").localeCompare(b.name || "");
    });

  return (
    <div data-testid="bul-departments">
      <PageHero eyebrow="Academics" title="Departments" />
      <PageBody>
        {list.length === 0 ? (
          <div className="text-center bg-white p-16 border border-[#D9D2C1] text-[#726C61]">
            No active departments are published right now. Please check back soon.
          </div>
        ) : (
          <div className="grid md:grid-cols-[300px_1fr] gap-6">
            {/* Left: list of departments */}
            <aside
              className="bg-white border border-[#D9D2C1] self-start"
              data-testid="bul-departments-list"
            >
              <div className="px-5 py-4 border-b border-[#D9D2C1]">
                <div className="text-[10px] tracking-[0.35em] uppercase text-[#FDBB30] font-semibold">
                  Browse
                </div>
                <div className="mt-1 text-lg text-[#0F0F10]" style={{ fontFamily: FONT.serif, fontWeight: 700 }}>
                  All Departments ({list.length})
                </div>
              </div>
              <ul>
                {list.map((d, i) => {
                  const isActive = d.id === active?.id;
                  return (
                    <li key={d.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveId(d.id);
                          setLbIdx(-1);
                        }}
                        data-testid={`bul-dept-tab-${d.id}`}
                        className={`w-full text-left px-5 py-3 flex items-center gap-3 border-l-4 transition-colors ${
                          isActive
                            ? "bg-[#F8F5EE] border-[#FDBB30]"
                            : "border-transparent hover:bg-[#F8F5EE]"
                        }`}
                      >
                        <span className="w-8 h-8 bg-[#0F0F10] text-[#FDBB30] flex items-center justify-center shrink-0">
                          <BookOpen size={14} strokeWidth={1.6} />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span
                            className={`block text-sm leading-snug ${
                              isActive
                                ? "text-[#0F0F10] font-semibold"
                                : "text-[#2A2A2B]"
                            }`}
                          >
                            {d.name}
                          </span>
                          {d.code && (
                            <span className="block text-[10px] text-[#726C61] font-mono uppercase tracking-wider mt-0.5">
                              {d.code}
                            </span>
                          )}
                        </span>
                        <ChevronRight
                          size={14}
                          strokeWidth={1.6}
                          className={isActive ? "text-[#FDBB30]" : "text-[#D9D2C1]"}
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {/* Right: detail */}
            {active && (
              <article
                key={active.id}
                data-testid={`bul-dept-${active.id}`}
                className="bg-white border border-[#D9D2C1]"
              >
                <div className="bg-[#0F0F10] text-white p-8 border-b-4 border-[#FDBB30]">
                  <div className="w-12 h-12 border-2 border-[#FDBB30] flex items-center justify-center text-[#FDBB30] mb-4">
                    <BookOpen size={20} strokeWidth={1.6} />
                  </div>
                  <h2 className="text-3xl sm:text-4xl leading-tight" style={{ fontFamily: FONT.serif, fontWeight: 700 }}>
                    {active.name}
                  </h2>
                  {active.code && (
                    <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-[#FDBB30] font-mono font-semibold">
                      {active.code}
                    </div>
                  )}
                </div>
                <div className="p-7 sm:p-8">
                  <div className="flex flex-wrap gap-2 mb-5">
                    {active.course_type && (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 bg-[#0F0F10] text-white font-semibold">
                        {active.course_type}
                      </span>
                    )}
                    {active.approval_body && (
                      <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 bg-[#FDBB30] text-[#0F0F10] font-semibold">
                        <ShieldCheck size={10} strokeWidth={2.4} />
                        {active.approval_body}
                      </span>
                    )}
                  </div>
                  {active.head_of_department && (
                    <div className="flex items-center gap-2 text-sm text-[#2A2A2B] mb-5">
                      <User size={14} strokeWidth={1.6} className="text-[#FDBB30]" />
                      <span>
                        <span className="text-[#726C61]">HoD:</span> {active.head_of_department}
                      </span>
                    </div>
                  )}

                  {/* Description + Photos — side by side */}
                  <div
                    className={`grid gap-6 ${photos.length > 0 ? "lg:grid-cols-[1fr_auto]" : "grid-cols-1"}`}
                  >
                    <div className="min-w-0">
                      {active.about_html ? (
                        <div
                          className="prose prose-sm max-w-none text-[#2A2A2B] leading-[1.9]"
                          dangerouslySetInnerHTML={{ __html: active.about_html }}
                        />
                      ) : (
                        <div className="text-sm text-[#726C61] italic">
                          Department details coming soon.
                        </div>
                      )}
                    </div>

                    {photos.length > 0 && (
                      <div className="lg:w-[400px] shrink-0">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-[#FDBB30] font-semibold mb-3 flex items-center gap-1.5">
                          <ImageIcon size={11} strokeWidth={1.6} />
                          Glimpses ({photos.length})
                        </div>
                        <PhotoCarousel
                          photos={photos}
                          onPhotoClick={(idx) => setLbIdx(idx)}
                          name={active.name}
                          testidPrefix="bul-dept-photo"
                          accentClass="bg-[#FDBB30]"
                        />
                      </div>
                    )}
                  </div>

                  {/* Department staff */}
                  {deptStaff.length > 0 && (
                    <div className="mt-10">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#FDBB30] font-semibold mb-4 flex items-center gap-1.5">
                        <Users size={11} strokeWidth={1.6} />
                        Faculty & Staff ({deptStaff.length})
                      </div>
                      <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {deptStaff.map((s) => {
                          const isHOD = active?.head_of_department_id === s.id || isHodByDesignation(s);
                          return (
                          <li
                            key={s.id}
                            data-testid={`bul-dept-staff-${s.id}`}
                            className={`bg-[#F8F5EE] p-3 flex items-center gap-3 border-l-4 ${
                              isHOD ? "border-[#FDBB30] bg-white" : "border-transparent"
                            }`}
                          >
                            {s.photo_url ? (
                              <img
                                src={s.photo_url}
                                alt={s.name}
                                className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-sm shrink-0"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-[#EDE7DA] flex items-center justify-center text-[#726C61] shrink-0">
                                <User size={18} strokeWidth={1.6} />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-[#0F0F10] truncate flex items-center gap-1.5">
                                {s.name}
                                {isHOD && (
                                  <span
                                    data-testid={`bul-dept-hod-badge-${s.id}`}
                                    className="inline-block text-[9px] tracking-[0.2em] uppercase font-bold px-1.5 py-0.5 bg-[#FDBB30] text-[#0F0F10] shrink-0"
                                  >
                                    HOD
                                  </span>
                                )}
                              </div>
                              {s.designation && (
                                <div className="text-[11px] text-[#726C61] truncate">
                                  {s.designation}
                                </div>
                              )}
                            </div>
                          </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </article>
            )}
          </div>
        )}
      </PageBody>
      {active && lbIdx >= 0 && photos.length > 0 && (
        <Lightbox
          photos={photos.map((p) => ({ image_url: p.url, caption: active.name }))}
          idx={lbIdx}
          onIdx={setLbIdx}
          onClose={() => setLbIdx(-1)}
        />
      )}
    </div>
  );
}
